import styled from '@emotion/styled';
import {percentageFormatter} from '@dnhb/formatters';
import { useMemo } from 'react';
import {bool, func} from 'react-proptypes';
import {useSelector} from 'react-redux';
import useMedia from 'shared/hooks/useMedia';
import {Checkbox, SelectInput} from 'shared/inputs';
import {FilterDropdown, Info, LabelText, looptijdExplanation} from 'shared/components';
import {HeaderForm} from 'shared/forms';
import {ARRANGEMENT, CONTINUATION} from '../constants';
import MarketValueForm from './MarketValueForm';
import {RateDeviationType} from "../../../src/enumeration/rateDeviationType";
import * as EnergyLabel from "../../../src/enumeration/energyLabel";

const ALL_PROVIDERS_OPTION = [{ label: 'Alle verstrekkers', value: null, active: true }];

const SelectContainer = styled.div(props => ({
  display: 'flex',
  width: '60%',
  marginBottom: '16px',
  '> div': {
    width: 'calc(50% - 24px)',
    marginRight: '24px',
  },
  [`@media ${props.theme.mobile}`]: {
    flexDirection: 'column',
    width: '100%',
    '> div': {
      width: '100%',
      ':nth-of-type(2)': {
        marginTop: '32px',
      },
    },
  },
}));

const LooptijdrenteCheckbox = ({ prefer_auto_risk_class_reduction, changeSorting }) => (
  <div css={{ display: 'flex' }}>
    <Checkbox
      label="Toon producten met looptijdrente bovenaan"
      name="prefer_auto_risk_class_reduction"
      value={prefer_auto_risk_class_reduction}
      onChange={e => changeSorting('prefer_auto_risk_class_reduction', e.target.value)}
    />
    <Info title="Looptijdrente">{looptijdExplanation}</Info>
  </div>
);

const InputForm = props => {
  const {
    settings,
    input,
    output: { errors },
  } = useSelector(state => state);
  const {
    mobileFormOpen,
    mobileFormOpener,
    calculateMortageHandler,
    updateSendHandler,
    changeSorting,
    switchMobileCustomLtv,
  } = props;

  const { available_for, prefer_auto_risk_class_reduction, rate_deviations } = input;
  const { onMobile } = useMedia();

  const handleHeaderFormClick = () => {
    if (!mobileFormOpen) {
      calculateMortageHandler();
    }
    mobileFormOpener();
  };

  const onLtvChange = value => {
    if (input.mobileCustomLtv) switchMobileCustomLtv();
    updateSendHandler('ltv', parseInt(value, 10));
  };

  const provider_options = useMemo(
    () =>
      ALL_PROVIDERS_OPTION.concat(settings.mortgage_providers).filter(
        provider => input.available_for === CONTINUATION || provider.active
      ),
    [settings, input.available_for]
  );

  const energy_label_options = useMemo(
    () => {
      const allEnergyLabels = [{ label: '-', value: null, active: true }];

      EnergyLabel.EnergyLabelTranslations.forEach(
        (item, key) => {
          // Filtering them out since they get auto generated because they are in the EnergyLabel enum
          const filterLabels = [
            EnergyLabel.EnergyLabel.ENERGY_LABEL_ENERGY_NEUTRAL,
            EnergyLabel.EnergyLabel.ENERGY_LABEL_ZERO_ON_THE_METER,
            EnergyLabel.EnergyLabel.ENERGY_LABEL_UNKNOWN,
            EnergyLabel.EnergyLabel.ENERGY_LABEL_INVALID,
            EnergyLabel.EnergyLabel.ENERGY_LABEL_A_QUIN_PLUS,
            EnergyLabel.EnergyLabel.ENERGY_LABEL_A_QUAD_PLUS_WITH_ENERGY_PERFORMANCE_GUARANTEE,
          ]

          if (filterLabels.includes(key)) {
            return;
          }

          allEnergyLabels.push(
              { label: item, value: key, active: false }
          );
        }
      );

      return allEnergyLabels;
    },
    [settings, input.energy_label]
  );

  const tags = [
    {
      label: 'Aflosvorm',
      value: settings.meta.mortgage_types.find(type => type.value === input.mortgage_type).label,
    },
    {
      label: 'Verstrekkers',
      value: provider_options.find(provider => provider.value === input.selected_provider).label,
    },
    {
      label: 'Energielabel',
      value: energy_label_options.find(energyLabel => energyLabel.value === input.energy_label).label,
    },
    {
      label: 'Marktwaarde',
      value: percentageFormatter(input.ltv, 0),
    },
    {
      label: 'NHG',
      value: input.nhg,
    },
    {
      label: 'Looptijdrente bovenaan',
      value: prefer_auto_risk_class_reduction,
    },
    {
      label: 'Renteverleningen & boeterentes',
      value: available_for === CONTINUATION,
    },
    {
      label: 'Huisbankkorting',
      value: rate_deviations.includes(RateDeviationType.CHECKING_ACCOUNT),
    },
    {
      label: 'Offerterente korting',
      value: rate_deviations.includes(RateDeviationType.QUOTATION_INTEREST_DISCOUNT),
    },
    {
      label: 'Recreatiewoning',
      value: rate_deviations.includes(RateDeviationType.RECREATION_HOUSE),
    }
  ];

  return (
    <HeaderForm
      buttonTitle={mobileFormOpen ? 'Berekening aanpassen' : ' Toon overzicht'}
      tags={tags}
      setMobileFormOpen={handleHeaderFormClick}
      mobileFormOpen={mobileFormOpen}
      hasErrors={errors === true}
    >
      <div css={{ display: 'flex' }}>
        <SelectContainer>
          <div>
            <LabelText>Aflosvorm</LabelText>
            <SelectInput
              options={settings.meta.mortgage_types}
              value={input.mortgage_type}
              onChange={(option) => {
                updateSendHandler('mortgage_type', option.value);

                // Default set LTV to 50% when mortgage_type is set to 10 (bridging_loan)
                if (option.value === 10) {
                  updateSendHandler('ltv', 50);

                  const temp = [...input.frt];
                  const indexOf = input.frt.indexOf(12);
                  if (indexOf > 0) {
                    temp.splice(indexOf, 1, temp[0]);
                  }
                  temp.splice(0, 1, 12);
                  updateSendHandler('frt', temp);
                  changeSorting('sorted_frt', temp[0]);
                }
              }}
              noOptionsMessage={() => 'Geen resultaten'}
              placeholder="Aflosvorm"
            />
          </div>
          <div>
            <LabelText>Verstrekkers</LabelText>
            <SelectInput
              placeholder="Verstrekkers"
              options={provider_options}
              value={input.selected_provider}
              onChange={option => updateSendHandler('selected_provider', option.value)}
              noOptionsMessage={() => 'Geen resultaten'}
              isSearchable
            />
          </div>
          <div>
            <LabelText>Energielabelkorting</LabelText>
            <SelectInput
                placeholder="Kies energielabel"
                options={energy_label_options}
                value={input.energy_label}
                onChange={option => updateSendHandler('energy_label', option.value)}
                noOptionsMessage={() => 'Geen resultaten'}
            />
          </div>
        </SelectContainer>
        {!onMobile && (
          <FilterDropdown css={{ marginTop: 34 }}>
            <LooptijdrenteCheckbox {...{ prefer_auto_risk_class_reduction, changeSorting }} />
            <Checkbox
              label="Toon rentes voor renteverlengingen & boeterentes"
              name="available_for"
              value={available_for === CONTINUATION}
              onChange={e => updateSendHandler('available_for', e.target.value ? CONTINUATION : ARRANGEMENT)}
            />
            <Checkbox
              label="Toon huisbankkorting"
              name="bank_discount"
              value={rate_deviations.includes(RateDeviationType.CHECKING_ACCOUNT)}
              onChange={
                e => {
                  if (e.currentTarget.checked) {
                    updateSendHandler('rate_deviations', [...rate_deviations, RateDeviationType.CHECKING_ACCOUNT]);

                    return;
                  }

                  rate_deviations.splice(rate_deviations.indexOf(RateDeviationType.CHECKING_ACCOUNT));
                  updateSendHandler('rate_deviations', rate_deviations);
                }}
            />
            <Checkbox
              label="Toon offerterente korting"
              name="quotation_interest_discount"
              value={rate_deviations.includes(RateDeviationType.QUOTATION_INTEREST_DISCOUNT)}
              onChange={
                e => {
                  if (e.currentTarget.checked) {
                    updateSendHandler('rate_deviations', [...rate_deviations, RateDeviationType.QUOTATION_INTEREST_DISCOUNT]);

                    return;
                  }

                  rate_deviations.splice(rate_deviations.indexOf(RateDeviationType.QUOTATION_INTEREST_DISCOUNT));
                  updateSendHandler('rate_deviations', rate_deviations);
                }}
            />
            <Checkbox
              label="Toon recreatiewoning"
              name="recreation_house"
              value={rate_deviations.includes(RateDeviationType.RECREATION_HOUSE)}
              onChange={
                e => {
                  if (e.currentTarget.checked) {
                    updateSendHandler('rate_deviations', [...rate_deviations, RateDeviationType.RECREATION_HOUSE]);

                    return;
                  }

                  rate_deviations.splice(rate_deviations.indexOf(RateDeviationType.RECREATION_HOUSE));
                  updateSendHandler('rate_deviations', rate_deviations);
                }}
            />
          </FilterDropdown>
        )}
      </div>

      <MarketValueForm {...props} onLtvChange={onLtvChange} />

      {onMobile && (
        <div css={{ paddingTop: '8px', borderTop: '1px dashed rgba(0,0,0,.5)', '> div': { marginTop: '16px' } }}>
          <LooptijdrenteCheckbox {...{ prefer_auto_risk_class_reduction, changeSorting }} />
          <Checkbox
            label="Toon rentes voor renteverlengingen & boeterentes"
            name="available_for"
            value={available_for === CONTINUATION}
            onChange={e => updateSendHandler('available_for', e.target.value ? CONTINUATION : ARRANGEMENT)}
          />
        </div>
      )}
    </HeaderForm>
  );
};

InputForm.defaultProps = {
  mobileFormOpen: true,
  ltvDropdown: false,
  calculateMortageHandler: () => {},
};

InputForm.propTypes = {
  ltvDropdown: bool,
  ltvDropdownOpener: func.isRequired,
  calculateMortageHandler: func,
  updateSendHandler: func.isRequired,
  changeSorting: func.isRequired,
  switchMobileCustomLtv: func.isRequired,
};

export default InputForm;
